import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Plus, Sparkles, Briefcase, MapPin, LayoutGrid, List as ListIcon, Users, Star, Power, Copy, Flag,
  Video, BarChart3, Send, RefreshCw, ChevronLeft, ChevronRight, Infinity as InfinityIcon,
  Settings as SettingsIcon, Trash2, Link2, X, Zap,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import VoiceInput from "@/components/VoiceInput";

const STATUS_STYLES = {
  OPEN: "bg-teal/15 text-teal border border-teal/30",
  ACTIVE: "bg-teal/15 text-teal border border-teal/30",
  INACTIVE: "bg-red-100 text-red-600 border border-red-200",
  CLOSED: "bg-neutral-100 text-neutral-600 border border-neutral-200",
};
const isActive = (s) => s === "OPEN" || s === "ACTIVE";
const fmtDate = (iso) => iso ? new Date(iso).toLocaleString(undefined, { month: "numeric", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }) : "—";

function CopyBtn({ text, label = "Copy link", testId }) {
  const [copied, setCopied] = useState(false);
  const doCopy = async (e) => {
    e.preventDefault(); e.stopPropagation();
    try { await navigator.clipboard.writeText(text); setCopied(true); toast.success("Link copied"); setTimeout(() => setCopied(false), 1500); }
    catch { toast.error("Copy failed"); }
  };
  return (
    <button onClick={doCopy} data-testid={testId} className="text-pink hover:underline text-xs font-semibold">
      {copied ? "Copied ✓" : label}
    </button>
  );
}

export default function JobsList() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", department: "Engineering", location: "Remote", work_mode: "Remote", description: "", requirements: "" });
  const [aiBusy, setAiBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState(() => localStorage.getItem("jobs_view") || "grid");
  const [selected, setSelected] = useState(new Set());
  const [sortBy, setSortBy] = useState("updated");
  const [perPage, setPerPage] = useState(10);
  const [page, setPage] = useState(1);
  const [views, setViews] = useState([]);
  const [activeViewId, setActiveViewId] = useState(null);
  const [flagJob, setFlagJob] = useState(null);
  const [flagReason, setFlagReason] = useState("");

  const setViewPersist = (v) => { setView(v); localStorage.setItem("jobs_view", v); };
  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/recruit/jobs"); setJobs(r.data); }
    catch (e) { toast.error("Failed to load jobs"); }
    finally { setLoading(false); }
  };
  const loadViews = () => api.get("/recruit/views?scope=jobs").then(r => setViews(r.data)).catch(() => {});
  useEffect(() => { load(); loadViews(); }, []);

  const sorted = useMemo(() => {
    const arr = [...jobs];
    const key = { updated: "created_at", title: "title", status: "status", candidates: "candidate_count" }[sortBy] || "created_at";
    arr.sort((a, b) => {
      if (key === "title") return (a[key] || "").localeCompare(b[key] || "");
      if (key === "candidates") return (b.candidate_count || 0) - (a.candidate_count || 0);
      return (b[key] || "").localeCompare(a[key] || "");
    });
    return arr;
  }, [jobs, sortBy]);
  const totalPages = Math.max(1, Math.ceil(sorted.length / perPage));
  const paged = sorted.slice((page - 1) * perPage, page * perPage);
  useEffect(() => { setPage(1); }, [perPage, sortBy]);

  const toggleSelect = (id) => setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const clearSelected = () => setSelected(new Set());

  const generateJD = async () => {
    if (!form.title) { toast.error("Enter a job title first"); return; }
    setAiBusy(true);
    try {
      const reqs = form.requirements.split(",").map(s => s.trim()).filter(Boolean);
      const { data } = await api.post("/ai/generate-jd", { title: form.title, requirements: reqs });
      setForm({ ...form, description: data.description });
      toast.success("AI job description generated");
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || "AI failed"); }
    finally { setAiBusy(false); }
  };
  const save = async () => {
    setSaving(true);
    try {
      const reqs = form.requirements.split(",").map(s => s.trim()).filter(Boolean);
      await api.post("/recruit/jobs", { ...form, requirements: reqs });
      setOpen(false);
      setForm({ title: "", department: "Engineering", location: "Remote", work_mode: "Remote", description: "", requirements: "" });
      toast.success("Job created"); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || "Create failed"); }
    finally { setSaving(false); }
  };

  const toggleStatus = async (job) => {
    const next = isActive(job.status) ? "INACTIVE" : "OPEN";
    try {
      await api.patch(`/recruit/jobs/${job.job_id}/status`, { status: next });
      toast.success(next === "OPEN" ? "Job activated" : "Job deactivated");
      load();
    } catch (e) { toast.error("Status update failed"); }
  };
  const duplicate = async (job) => {
    try {
      const { data } = await api.post(`/recruit/jobs/${job.job_id}/duplicate`);
      toast.success(`Duplicated as "${data.title}"`); load();
    } catch (e) { toast.error("Duplicate failed"); }
  };
  const submitFlag = async () => {
    if (!flagReason.trim()) { toast.error("Add a short reason"); return; }
    try {
      await api.post(`/recruit/jobs/${flagJob.job_id}/flag`, { reason: flagReason.trim(), severity: "warning" });
      toast.success("Job flagged for review");
      setFlagJob(null); setFlagReason("");
    } catch (e) { toast.error("Flag failed"); }
  };
  const bulkAction = async (action) => {
    if (selected.size === 0) return;
    try {
      const { data } = await api.post("/recruit/jobs-bulk", { job_ids: [...selected], action });
      toast.success(`${action}: ${data.modified} job${data.modified !== 1 ? "s" : ""}`);
      clearSelected(); load();
    } catch (e) { toast.error(`Bulk ${action} failed`); }
  };
  const saveCurrentView = async () => {
    const name = window.prompt("Name this view", "Active + Recent");
    if (!name) return;
    try {
      await api.post("/recruit/views", { name, scope: "jobs", filters: { sortBy, view }, pinned: true });
      toast.success("View saved"); loadViews();
    } catch (e) { toast.error("Save view failed"); }
  };
  const togglePinView = async (v) => {
    try { const { data } = await api.patch(`/recruit/views/${v.view_id}/pin`); toast.success(data.pinned ? "Pinned" : "Unpinned"); loadViews(); }
    catch { toast.error("Pin toggle failed"); }
  };

  // ---- Card renderer ----
  const JobCard = ({ j }) => {
    const activeSel = selected.has(j.job_id);
    return (
      <Card className={`border shadow-sm hover:shadow-md transition-all ${activeSel ? "ring-2 ring-pink" : ""}`} data-testid={`job-card-${j.job_id}`}>
        <CardContent className="p-5">
          <div className="flex items-start justify-between gap-2">
            <Link to={`/app/recruit/jobs/${j.job_id}`} className="flex items-start gap-3 min-w-0 flex-1">
              <div className="w-10 h-10 rounded-lg grid place-items-center shrink-0" style={{ backgroundColor: "#F5F5F7" }}>
                <Briefcase className="w-5 h-5 text-neutral-600" />
              </div>
              <div className="min-w-0">
                <div className="font-semibold text-base truncate">{j.title}</div>
                <div className="text-xs text-neutral-500 mt-0.5">
                  <span className="text-neutral-600 font-medium">({j.work_mode || "On-site"})</span> {j.location || "—"}
                </div>
              </div>
            </Link>
            <Checkbox checked={activeSel} onCheckedChange={() => toggleSelect(j.job_id)} data-testid={`job-select-${j.job_id}`} />
          </div>

          <TooltipProvider delayDuration={200}>
            <div className="flex items-center gap-1 mt-3">
              <Link to={`/app/recruit/jobs/${j.job_id}`}>
                <Button size="sm" variant="outline" className="rounded-md text-xs h-7 px-3" data-testid={`job-view-${j.job_id}`}>View</Button>
              </Link>
              {[
                { Icon: Send, label: "Invite candidate", href: `/app/recruit/jobs/${j.job_id}?invite=1`, testId: `job-invite-${j.job_id}` },
                { Icon: Video, label: "Video questions", href: `/app/recruit/jobs/${j.job_id}/settings`, testId: `job-video-${j.job_id}` },
                { Icon: BarChart3, label: "Analytics", href: `/app/analytics?job=${j.job_id}`, testId: `job-analytics-${j.job_id}` },
              ].map(({ Icon, label, href, testId }) => (
                <Tooltip key={label}>
                  <TooltipTrigger asChild>
                    <Link to={href}><Button size="icon" variant="ghost" className="h-7 w-7" data-testid={testId}><Icon className="w-3.5 h-3.5 text-neutral-500" /></Button></Link>
                  </TooltipTrigger>
                  <TooltipContent>{label}</TooltipContent>
                </Tooltip>
              ))}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => duplicate(j)} data-testid={`job-duplicate-${j.job_id}`}><Copy className="w-3.5 h-3.5 text-neutral-500" /></Button>
                </TooltipTrigger>
                <TooltipContent>Duplicate</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => { setFlagJob(j); setFlagReason(""); }} data-testid={`job-flag-${j.job_id}`}><Flag className="w-3.5 h-3.5 text-neutral-500" /></Button>
                </TooltipTrigger>
                <TooltipContent>Flag for review</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => toggleStatus(j)} data-testid={`job-toggle-${j.job_id}`}><Power className={`w-3.5 h-3.5 ${isActive(j.status) ? "text-teal" : "text-neutral-400"}`} /></Button>
                </TooltipTrigger>
                <TooltipContent>{isActive(j.status) ? "Deactivate" : "Activate"}</TooltipContent>
              </Tooltip>
            </div>
          </TooltipProvider>

          <div className="mt-4 space-y-1.5 text-xs">
            <div className="flex items-center gap-2"><span className="text-neutral-400 w-28">Status:</span><Badge className={`${STATUS_STYLES[j.status] || STATUS_STYLES.CLOSED} border rounded-full text-[10px] uppercase tracking-widest px-2`}>{isActive(j.status) ? "Active" : (j.status || "—")}</Badge></div>
            <div className="flex items-center gap-2"><span className="text-neutral-400 w-28">Manager:</span><span className="text-neutral-700 truncate">{j.manager_group || j.hiring_manager || "Pacific Everyone"}</span></div>
            <div className="flex items-center gap-2"><span className="text-neutral-400 w-28">Last interviewed:</span><span className="text-neutral-700">{fmtDate(j.last_interviewed_at)}</span></div>
            <div className="flex items-center gap-2"><span className="text-neutral-400 w-28">Updated at:</span><span className="text-neutral-700">{fmtDate(j.updated_at || j.created_at)}</span></div>
          </div>

          <div className="mt-3 pt-3 border-t text-xs space-y-1">
            <div className="flex items-center gap-2"><span className="text-neutral-400 w-28">Invite links:</span>
              <span className="text-neutral-500">One-Way:</span><CopyBtn text={j.oneway_invite_link || ""} label="Copy link" testId={`copy-oneway-${j.job_id}`} />
            </div>
            <div className="flex items-center gap-2"><span className="w-28" />
              <span className="text-neutral-500">Predictive:</span><CopyBtn text={j.predictive_invite_link || ""} label="Copy link" testId={`copy-predictive-${j.job_id}`} />
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-4" data-testid="jobs-list-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-neutral-500">Recruit</div>
          <h1 className="text-3xl font-bold mt-1">Jobs</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Link to="/app/recruit/job-settings">
            <Button variant="outline" className="rounded-full h-9" data-testid="jobs-settings-btn"><SettingsIcon className="w-3.5 h-3.5 mr-1.5" /> Job settings</Button>
          </Link>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="btn-primary rounded-full h-9" data-testid="new-job-btn"><Plus className="w-4 h-4 mr-1" /> Create new job</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader><DialogTitle>Create a new job</DialogTitle></DialogHeader>
              <div className="space-y-4 mt-2">
                <div>
                  <Label>Job title</Label>
                  <div className="flex gap-2 mt-1.5 items-center">
                    <Input data-testid="new-job-title" value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} placeholder="e.g., Staff Engineer" />
                    <VoiceInput onTranscript={(t) => setForm(f => ({...f, title: t}))} testId="new-job-title-mic" />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div><Label>Department</Label><Input value={form.department} onChange={(e) => setForm({...form, department: e.target.value})} className="mt-1.5" /></div>
                  <div><Label>Location</Label><Input value={form.location} onChange={(e) => setForm({...form, location: e.target.value})} className="mt-1.5" placeholder="Australia" /></div>
                  <div>
                    <Label>Work mode</Label>
                    <Select value={form.work_mode} onValueChange={(v) => setForm({...form, work_mode: v})}>
                      <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="On-site">On-site</SelectItem>
                        <SelectItem value="Remote">Remote</SelectItem>
                        <SelectItem value="Hybrid">Hybrid</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div><Label>Key requirements (comma‑separated)</Label>
                  <Input data-testid="new-job-requirements" value={form.requirements} onChange={(e) => setForm({...form, requirements: e.target.value})} className="mt-1.5" placeholder="React, Python, 5+ yrs" />
                </div>
                <div>
                  <div className="flex justify-between items-center mb-1.5"><Label>Description</Label>
                    <div className="flex items-center gap-2">
                      <VoiceInput onTranscript={(t) => setForm(f => ({...f, description: f.description ? `${f.description} ${t}` : t}))} testId="new-job-desc-mic" />
                      <Button variant="ghost" size="sm" onClick={generateJD} disabled={aiBusy} data-testid="ai-generate-jd-btn" className="text-pink">
                        <Sparkles className="w-3.5 h-3.5 mr-1" /> {aiBusy ? "Thinking…" : "Generate with AI"}
                      </Button>
                    </div>
                  </div>
                  <Textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} rows={8} placeholder="Speak, type, or generate with AI ↑" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
                <Button className="btn-primary" onClick={save} disabled={saving || !form.title} data-testid="save-job-btn">{saving ? "Saving…" : "Create job"}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Pinned views + slot indicator */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap" data-testid="jobs-views">
          <button onClick={() => setActiveViewId(null)} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm ${activeViewId === null ? "bg-navy/5 text-navy font-semibold" : "text-neutral-600 hover:bg-neutral-50"}`} data-testid="view-all">
            <span>All</span>
            <Star className="w-3.5 h-3.5 text-neutral-300" />
          </button>
          {views.filter(v => v.pinned).map(v => (
            <button key={v.view_id} onClick={() => setActiveViewId(v.view_id)} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm ${activeViewId === v.view_id ? "bg-navy/5 text-navy font-semibold" : "text-neutral-600 hover:bg-neutral-50"}`} data-testid={`view-${v.view_id}`}>
              <span>{v.name}</span>
              <Star className="w-3.5 h-3.5 text-pink fill-pink" onClick={(e) => { e.stopPropagation(); togglePinView(v); }} />
            </button>
          ))}
          <Button variant="ghost" size="sm" onClick={saveCurrentView} className="text-xs text-neutral-500" data-testid="save-view-btn">+ Save view</Button>
        </div>
        <div className="inline-flex items-center gap-1.5 text-teal font-semibold text-sm" data-testid="job-slots-indicator">
          <InfinityIcon className="w-4 h-4" /> Unlimited job slots remaining
        </div>
      </div>

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <Card className="border-2 border-pink shadow-sm" data-testid="bulk-actions-bar">
          <CardContent className="p-3 flex items-center justify-between gap-3">
            <div className="text-sm font-semibold text-pink">{selected.size} selected</div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={() => bulkAction("activate")} data-testid="bulk-activate">Activate</Button>
              <Button size="sm" variant="outline" onClick={() => bulkAction("deactivate")} data-testid="bulk-deactivate">Deactivate</Button>
              <Button size="sm" variant="outline" onClick={() => bulkAction("duplicate")} data-testid="bulk-duplicate">Duplicate</Button>
              <Button size="sm" variant="ghost" className="text-red-500" onClick={() => { if (confirm(`Delete ${selected.size} job(s)?`)) bulkAction("delete"); }} data-testid="bulk-delete"><Trash2 className="w-3.5 h-3.5 mr-1" /> Delete</Button>
              <Button size="sm" variant="ghost" onClick={clearSelected} data-testid="bulk-clear"><X className="w-3.5 h-3.5" /></Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Toolbar row */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-y py-2">
        <div className="flex items-center gap-2">
          <Select value={String(perPage)} onValueChange={(v) => setPerPage(parseInt(v))}>
            <SelectTrigger className="h-8 text-xs w-32" data-testid="per-page-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {[10, 20, 50, 100].map(n => <SelectItem key={n} value={String(n)}>{n} per page</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="h-8 text-xs w-40" data-testid="sort-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="updated">Updated at</SelectItem>
              <SelectItem value="title">Title A→Z</SelectItem>
              <SelectItem value="status">Status</SelectItem>
              <SelectItem value="candidates">Most candidates</SelectItem>
            </SelectContent>
          </Select>
          <div className="inline-flex items-center rounded-md bg-white border p-0.5" data-testid="jobs-view-toggle">
            <button onClick={() => setViewPersist("grid")} className={`px-2 py-1 rounded text-xs flex items-center gap-1 ${view === "grid" ? "bg-neutral-100" : "text-neutral-500"}`} data-testid="jobs-view-grid"><LayoutGrid className="w-3.5 h-3.5" /></button>
            <button onClick={() => setViewPersist("list")} className={`px-2 py-1 rounded text-xs flex items-center gap-1 ${view === "list" ? "bg-neutral-100" : "text-neutral-500"}`} data-testid="jobs-view-list"><ListIcon className="w-3.5 h-3.5" /></button>
          </div>
          <div className="text-xs text-neutral-500 ml-2" data-testid="jobs-pagination-info">{sorted.length ? `${(page - 1) * perPage + 1}-${Math.min(page * perPage, sorted.length)} of ${sorted.length}` : "0 of 0"}</div>
          <Button size="icon" variant="ghost" className="h-7 w-7" disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))} data-testid="page-prev"><ChevronLeft className="w-4 h-4" /></Button>
          <Button size="icon" variant="ghost" className="h-7 w-7" disabled={page >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))} data-testid="page-next"><ChevronRight className="w-4 h-4" /></Button>
          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={load} data-testid="refresh-btn"><RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /></Button>
        </div>
      </div>

      {/* Cards / list */}
      {paged.length === 0 ? (
        <div className="text-neutral-500 text-center py-16">{loading ? "Loading…" : "No jobs yet. Create your first job to get started."}</div>
      ) : view === "grid" ? (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="jobs-grid">
          {paged.map((j) => <JobCard key={j.job_id} j={j} />)}
        </div>
      ) : (
        <div className="space-y-3" data-testid="jobs-list">
          {paged.map((j) => <JobCard key={j.job_id} j={j} />)}
        </div>
      )}

      {/* Flag dialog */}
      <Dialog open={!!flagJob} onOpenChange={(v) => !v && setFlagJob(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Flag "{flagJob?.title}" for review</DialogTitle>
            <DialogDescription>Alfred admins will see this in the compliance audit log. Use for wrong info, biased language, or duplicate posts.</DialogDescription>
          </DialogHeader>
          <Textarea rows={4} value={flagReason} onChange={(e) => setFlagReason(e.target.value)} placeholder="What's off about this job?" data-testid="flag-reason-input" />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setFlagJob(null)}>Cancel</Button>
            <Button className="bg-amber-500 hover:bg-amber-600 text-white" onClick={submitFlag} data-testid="flag-submit-btn"><Flag className="w-3.5 h-3.5 mr-1.5" /> Submit flag</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
